import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import RecipeReadOnlyView from "../../recetas/components/RecipeReadOnlyView";
import {
  getRecipe,
  getRecipeVersion,
  getRecipeVersions,
} from "../../recetas/api/recipesApi";

function listFromPaginated(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response)) return response;
  return [];
}

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message)
    ? message.join(" | ")
    : message || "No se pudo cargar la receta.";
}

export default function SaleProductRecipeDetailModal({
  open,
  recipeId,
  recipeName,
  onClose,
}) {
  const [recipe, setRecipe] = useState(null);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const numericRecipeId = Number(recipeId);
    if (!open || !Number.isSafeInteger(numericRecipeId) || numericRecipeId <= 0) {
      setRecipe(null);
      setVersion(null);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");

    Promise.all([
      getRecipe(numericRecipeId, { signal: controller.signal }),
      getRecipeVersions(numericRecipeId, {
        page: 1,
        limit: 1,
        signal: controller.signal,
      }),
    ])
      .then(async ([recipeData, versionsResponse]) => {
        if (controller.signal.aborted) return;
        setRecipe(recipeData);
        const activeVersion = listFromPaginated(versionsResponse)[0] || null;
        if (!activeVersion?.id) {
          setVersion(null);
          return;
        }
        const versionData = await getRecipeVersion(
          numericRecipeId,
          activeVersion.id,
          { signal: controller.signal }
        );
        if (!controller.signal.aborted) setVersion(versionData);
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        setRecipe(null);
        setVersion(null);
        setError(errorToMessage(requestError));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, recipeId, retry]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: {
            bgcolor: "rgba(2, 8, 23, 0.45)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
      <Fade in={Boolean(open)} timeout={180}>
        <Box
          sx={{
            width: "min(96vw, 1040px)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between", mb: 1.6 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                title={recipe?.name || recipeName}
                sx={{ fontSize: 20, fontWeight: 800, color: "primary.900" }}
              >
                {recipe?.name || recipeName || "Detalle de receta"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "neutral.700" }}>
                Detalle de receta
              </Typography>
            </Box>
            <IconButton type="button" size="small" onClick={onClose} aria-label="Cerrar detalle de receta">
              <CloseIcon sx={{ color: "primary.900" }} />
            </IconButton>
          </Stack>

          <Box sx={{ minHeight: 0, overflowY: "auto", borderRadius: "22px" }}>
            {error ? (
              <Stack spacing={1.5} sx={{ minHeight: 260, p: 2, bgcolor: "neutral.50", justifyContent: "center" }}>
                <Alert severity="error">{error}</Alert>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => setRetry((value) => value + 1)}
                  sx={{ alignSelf: "flex-end", borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
                >
                  Reintentar
                </Button>
              </Stack>
            ) : loading && !recipe ? (
              <Stack spacing={1} sx={{ minHeight: 260, bgcolor: "neutral.50", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Cargando receta...
                </Typography>
              </Stack>
            ) : (
              <RecipeReadOnlyView recipe={recipe} version={version} loading={loading} />
            )}
          </Box>
        </Box>
      </Fade>
    </Dialog>
  );
}
